import { getPlanFullI18nKey } from "common/elements";
import i18n from "features/parent/i18n";
import { PlanType } from "features/subscription";
import { PlanCategory, BillingPeriod } from "features/subscription/useNativePackages";

interface PlanComparisonTableProps {
  plan: PlanType;
  billingPeriod: BillingPeriod;
  getPriceDisplay: (
    planCategory: PlanCategory,
    period: BillingPeriod,
  ) => { price: number; priceString: string; currencyCode: string };
}

export function PlanComparisonTable({
  plan,
  billingPeriod,
  getPriceDisplay,
}: PlanComparisonTableProps) {
  const isFree = plan === PlanType.FREE;

  return (
    <div className="bg-white rounded-[16px] p-6">
      <div className="flex-row-el items-center gap-2 text-[27px] text-textcolor-black mb-5 pb-3 border-b border-outline">
        <span className="material-symbols-outlined !text-[45px] text-red-100">compare</span>
        {i18n.t("MSG_PARENT_PAGE_PLAN_COMPARISON")}
      </div>

      <table className="w-full border-collapse">
        <colgroup>
          <col style={{ width: "28%" }} />
          <col style={{ width: "24%" }} />
          <col style={{ width: "24%" }} />
          <col style={{ width: "24%" }} />
        </colgroup>
        <thead>
          <tr>
            <th className="py-4 px-3 text-left text-[24px] text-gray2-100 border-b border-outline"></th>
            <th className="py-4 px-3 text-center text-[24px] text-white border-b border-outline bg-[#4C9DF1] rounded-tl-[12px]">
              {i18n.t("MSG_PLAN_FREE_FULL_PARENT")}
            </th>
            <th className="py-4 px-3 text-center text-[24px] text-white border-b border-outline bg-[#66BB6A]">
              {i18n.t("MSG_PLAN_STANDARD_FULL_PARENT")}
            </th>
            <th className="py-4 px-3 text-center text-[24px] text-white border-b border-outline bg-[#FF5722] rounded-tr-[12px]">
              {i18n.t("MSG_PLAN_PREMIUM_FULL_PARENT")}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              colSpan={4}
              className="py-3 px-3 text-left text-[20px] text-gray2-100 bg-gray2-20 border-b border-outline"
            >
              {i18n.t("MSG_PARENT_PAGE_PLAN_SCOPE_ACCOUNT")}
            </td>
          </tr>
          <tr>
            <td className="py-4 px-3 text-left text-[22px] text-textcolor-black border-b border-outline">
              {i18n.t("MSG_PARENT_PAGE_USER_COUNT")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline">
              {i18n.t("MSG_PARENT_PAGE_USERS_FREE")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline bg-[#66BB6A]/10">
              {i18n.t("MSG_PARENT_PAGE_USERS_STANDARD")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline bg-[#FF5722]/10">
              {i18n.t("MSG_PARENT_PAGE_USERS_PREMIUM")}
            </td>
          </tr>
          <tr>
            <td className="py-4 px-3 text-left text-[22px] text-textcolor-black border-b border-outline">
              {i18n.t("MSG_PARENT_PAGE_STORAGE_FEATURE")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline">
              {i18n.t("MSG_PARENT_PAGE_STORAGE_FREE_LIMIT")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline bg-[#66BB6A]/10">
              {i18n.t("MSG_PARENT_PAGE_STORAGE_STANDARD_LIMIT")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline bg-[#FF5722]/10">
              {i18n.t("MSG_PARENT_PAGE_STORAGE_PREMIUM_LIMIT")}
            </td>
          </tr>
          <tr>
            <td
              colSpan={4}
              className="py-3 px-3 text-left text-[20px] text-gray2-100 bg-gray2-20 border-b border-outline"
            >
              {i18n.t("MSG_PARENT_PAGE_PLAN_SCOPE_PLAYER")}
            </td>
          </tr>
          <tr>
            <td className="py-4 px-3 text-left text-[22px] text-textcolor-black border-b border-outline">
              {i18n.t("MSG_PARENT_PAGE_PROJECT_COUNT")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline">
              5{i18n.t("MSG_PARENT_PAGE_PROJECTS")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline bg-[#66BB6A]/10">
              10{i18n.t("MSG_PARENT_PAGE_PROJECTS")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline bg-[#FF5722]/10">
              10{i18n.t("MSG_PARENT_PAGE_PROJECTS")}
            </td>
          </tr>
          <tr>
            <td className="py-4 px-3 text-left text-[22px] text-textcolor-black border-b border-outline">
              {i18n.t("MSG_PARENT_PAGE_AI_CHAT_FEATURE")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline">
              {i18n.t("MSG_PARENT_PAGE_AI_CHAT_FREE_LIMIT")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline bg-[#66BB6A]/10">
              {i18n.t("MSG_PARENT_PAGE_UNLIMITED")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline bg-[#FF5722]/10">
              {i18n.t("MSG_PARENT_PAGE_UNLIMITED")}
            </td>
          </tr>
          <tr>
            <td className="py-4 px-3 text-left text-[22px] text-textcolor-black border-b border-outline">
              {i18n.t("MSG_PARENT_PAGE_AI_IMAGE_FEATURE")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline">
              {i18n.t("MSG_PARENT_PAGE_AI_IMAGE_FREE_LIMIT")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline bg-[#66BB6A]/10">
              {i18n.t("MSG_PARENT_PAGE_AI_IMAGE_STANDARD_LIMIT")}
            </td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black border-b border-outline bg-[#FF5722]/10">
              {i18n.t("MSG_PARENT_PAGE_AI_IMAGE_PREMIUM_LIMIT")}
            </td>
          </tr>
          <tr>
            <td className="py-4 px-3"></td>
            <td className="py-4 px-3 text-center text-[22px] text-textcolor-black">
              {isFree ? (
                <span className="text-[#4C9DF1]">
                  {i18n.t(getPlanFullI18nKey(PlanType.FREE, true))}
                </span>
              ) : (
                "-"
              )}
            </td>
            <td className="py-4 px-3 text-center text-[22px] bg-[#66BB6A]/10">
              {plan === PlanType.STANDARD ? (
                <span className="text-[#66BB6A]">
                  {i18n.t(getPlanFullI18nKey(PlanType.STANDARD, true))}
                </span>
              ) : (
                "-"
              )}
            </td>
            <td className="py-4 px-3 text-center text-[22px] bg-[#FF5722]/10">
              {plan === PlanType.PREMIUM ? (
                <span className="text-[#FF5722]">
                  {i18n.t(getPlanFullI18nKey(PlanType.PREMIUM, true))}
                </span>
              ) : (
                "-"
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
